package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBackendDrivenUiForKotlinMultiplatformPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/BackendDrivenUiForKotlinMultiplatform.md", FrontMatterElement.Builder { addScalar("title", "Backend-driven UI for Kotlin Multiplatform"); addScalar("searchTitle", "Backend-driven UI (server-driven UI) toolkit for Kotlin Multiplatform"); addScalar("date", "2026-08-27"); addScalar("description", "kompot: the server describes a screen as a tree of components, the client renders it, and a new screen ships without a client release. Open hierarchies, KSP registration, and two conformance kits."); addScalar("library", "kompot"); }))
}

@Page("/blog/backend-driven-ui-for-kotlin-multiplatform")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun BackendDrivenUiForKotlinMultiplatformPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kompot") {
            org.jetbrains.compose.web.dom.Text("kompot")
        }
        org.jetbrains.compose.web.dom.Text(" is a backend-driven UI toolkit for Kotlin Multiplatform: the server describes a screen as a serialisable tree of components, the client renders it, and a new screen ships without a new client release. The protocol modules publish for the JVM, Android, iOS and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("wasmJs")
        }
        org.jetbrains.compose.web.dom.Text("; the Compose renderers for all of those minus the Intel simulator.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Backend-driven UI is easy to start and hard to keep honest. The usual failure is a toolkit that knows your components: a sealed hierarchy of every widget the product happens to have, a client that must be released whenever the server learns a new one, and a serialiser that turns an unfamiliar type into a crash on exactly the installs a staged rollout cannot reach.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("kompot is built the other way round. The core knows about a tree, actions, modifiers and design tokens — and nothing about what a \"product card\" is. Everything concrete arrives as a plug-in module, and an unknown type degrades to a placeholder instead of taking the screen down. The degradation is observable: a sink receives every component the client could not draw and every action it could not act on, because a crash is reported by every crash reporter ever written and a silent hole is reported by nobody.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Open contracts")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("KompotComponent")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("KompotAction")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FieldValue")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ValidationRule")
            }
            org.jetbrains.compose.web.dom.Text(" are interfaces; the application assembles the concrete set.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Generated registration")
            }
            org.jetbrains.compose.web.dom.Text(" — one ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("@KompotComponentMarker")
            }
            org.jetbrains.compose.web.dom.Text(" and KSP writes the polymorphic registration and the renderer entry; no hand-maintained list.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Forms that stay client-side")
            }
            org.jetbrains.compose.web.dom.Text(" — validation, visibility and cross-field rules run locally; a server-relevant change asks the backend for a targeted patch instead of a new screen.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Server-driven theming")
            }
            org.jetbrains.compose.web.dom.Text(" — open string tokens with a theme that says what to paint them with, down to text colour, without touching the shapes a design system owns.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Live updates")
            }
            org.jetbrains.compose.web.dom.Text(" — a screen names its own update channel, so updates stay per-user.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Multi-step flows as pure functions")
            }
            org.jetbrains.compose.web.dom.Text(" — a wizard graph is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("(session, transition, draft) -> session")
            }
            org.jetbrains.compose.web.dom.Text(", unit-testable with no HTTP in sight.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

val kompotVersion = "0.33.1.88"

dependencies {
    implementation(platform("io.github.youndie:kompot-bom:${'$'}kompotVersion"))
    implementation("io.github.youndie:kompot-core")
    implementation("io.github.youndie:kompot-standard")
    implementation("io.github.youndie:kompot-ktor")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The platform matters more than brevity: a version carries the CI run number, so any two publishes differ, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-core:0.19.0.26")
        }
        org.jetbrains.compose.web.dom.Text(" beside ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-client:0.19.0.27")
        }
        org.jetbrains.compose.web.dom.Text(" would resolve quietly into a combination nobody ever built. Through the BOM that combination cannot be written down.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-looks-like") }) {
        org.jetbrains.compose.web.dom.Text("What it looks like")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@Serializable
@SerialName("product_card")
@KompotComponentMarker
data class ProductCardComponent(
    override val id: String,
    val title: String,
    val onClick: KompotAction? = null,
) : KompotComponent
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""get("/catalogue") {
    call.respondKompotComponent(
        column {
            text("Catalogue", style = TypographyToken("title_large"))
            items.forEach { productCard(title = it.title, onClick = navigate(it.url)) }
        },
    )
}
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-the-contract-is-kept-honest") }) {
        org.jetbrains.compose.web.dom.Text("How the contract is kept honest")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The JSON Schema for every protocol module is generated from the same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SerialDescriptor")
        }
        org.jetbrains.compose.web.dom.Text("s kotlinx.serialization encodes a response with, so the schema cannot fall behind the types; the schemas, and the specification itself, travel inside the published artefact. The rules the schema cannot express are numbered, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KompotSpecResources.rules()")
        }
        org.jetbrains.compose.web.dom.Text(" resolves ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\"9.4.3\"")
        }
        org.jetbrains.compose.web.dom.Text(" to the sentence that states it — from the jar, not from a checkout.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two conformance kits hold the two sides of the wire. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-tck")
        }
        org.jetbrains.compose.web.dom.Text(" walks a running server over HTTP with twelve checks — schema conformance, form connectivity, ETag revalidation, pagination termination, idempotency, the update-stream framing. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-client-tck")
        }
        org.jetbrains.compose.web.dom.Text(" is a corpus of eighteen input-to-outcome cases a client answers through a seven-operation adapter, in any language; a case that asserts nothing is reported as a violation, and a coverage report says which of the 35 numbered rules of the forms section each case holds — 19 today, with every uncovered rule assigned to the instrument that does hold it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each release is also read back from the repository by ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/proba") {
            org.jetbrains.compose.web.dom.Text("proba")
        }
        org.jetbrains.compose.web.dom.Text(" the way a stranger's build resolves it; the verdict badge in the README is written by the same run that published the version it describes.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Compose renderers cover the standard, form, wizard and image components; the renderer of a component you invent is yours, and so is the SwiftUI side — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("kompot-swift-interop")
            }
            org.jetbrains.compose.web.dom.Text(" is a bridge over the ObjC export gaps, not a renderer.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The live-update contract is here; the SSE or WebSocket transport is not.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("form-core")
            }
            org.jetbrains.compose.web.dom.Text(" covers what a form can decide locally. Limits and balances belong to the server; the client only highlights the field a refusal names.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Compose half is built against Compose Multiplatform 1.11.1 with material3 ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1.11.0-alpha07")
            }
            org.jetbrains.compose.web.dom.Text(" — that line has no stable material3 — and mixing Compose lines fails at runtime, not at resolution.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("iosX64")
            }
            org.jetbrains.compose.web.dom.Text(" is unreachable for anything depending on Compose: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("compose.runtime")
            }
            org.jetbrains.compose.web.dom.Text(" last published it at ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1.11.0-alpha01")
            }
            org.jetbrains.compose.web.dom.Text(". Device and Apple-silicon simulator targets are there.")
        }
    }
}
